import { ReportStatus } from '@eppendorf/vnls-reporting-service-utils';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  REPORT_STATUS_FILTERS,
  ReportStatusFilter,
} from '$features/report-hub/components/status-filter';

export interface ReportSearchParamsState {
  statusFilter: ReportStatusFilter;
  apiStatus: ReportStatus | undefined;
  searchQuery: string;
  apiSearchQuery: string | undefined;
  handleSearchQueryChange: (value: string) => void;
  handleStatusChange: (status: ReportStatusFilter) => void;
  resetSearchParams: () => void;
}

export function useReportSearchParams(): ReportSearchParamsState {
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = searchParams.get('status');
  const statusFilter =
    statusParam && REPORT_STATUS_FILTERS.includes(statusParam as ReportStatusFilter)
      ? (statusParam as ReportStatusFilter)
      : ReportStatusFilter.All;

  const apiStatus =
    statusFilter === ReportStatusFilter.All ? undefined : (statusFilter as ReportStatus);

  const searchQueryParam = searchParams.get('searchQuery');
  const searchQuery = searchQueryParam || '';
  const apiSearchQuery = searchQueryParam || undefined;

  const handleSearchQueryChange = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          if (value) {
            prev.set('searchQuery', value);
          } else {
            prev.delete('searchQuery');
          }
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleStatusChange = useCallback(
    (status: ReportStatusFilter) => {
      setSearchParams(
        (prev) => {
          if (status === ReportStatusFilter.All) {
            prev.delete('status');
          } else {
            prev.set('status', status);
          }
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const resetSearchParams = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete('searchQuery');
      prev.delete('status');
      return prev;
    });
  }, [setSearchParams]);

  return {
    statusFilter,
    apiStatus,
    searchQuery,
    apiSearchQuery,
    handleSearchQueryChange,
    handleStatusChange,
    resetSearchParams,
  };
}
